import { useCallback, useEffect, useRef, useState, useContext } from "react";
import { AudioContext } from "../services/AudioContext";
import { useDoubleTap } from "./useDoubleTap";

export default function usePlayerControls() {
  const audioContext = useContext(AudioContext);

  const audioRef = audioContext?.audioRef ?? null;

  const { playbackState, setCurrentTimeSec } = useContext(AudioContext);

  const collapsedHeight = 200;
  const expandedHeightRatio = 0.85;
  const threshold = 80;
  const expandedHeight = window.innerHeight * expandedHeightRatio;
  const [height, setHeight] = useState(collapsedHeight);
  const [dragging, setDragging] = useState(false);

  const playerRef = useRef(null);
  const startYRef = useRef(0);
  const startHeightRef = useRef(collapsedHeight);
  const listRef = useRef(null);
  const [transitioning, setTransitioning] = useState(false);

  const progress = (height - collapsedHeight) / (expandedHeight - collapsedHeight);
  const collapsedOpacity = 1 - progress * 2;
  const expandedOpacity = progress;

  const animateHeight = (targetHeight) => {
    setTransitioning(false);

    void playerRef.current?.offsetHeight;

    setTransitioning(true);

    requestAnimationFrame(() => {
      setHeight(targetHeight);
    });

    setTimeout(() => setTransitioning(false), 300);
  };

  const onMouseDown = (e) => {
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);
    const offsetY = clientY - playerRef.current.getBoundingClientRect().top;

    if (height < expandedHeight || offsetY < collapsedHeight) {
      setDragging(true);
      startYRef.current = clientY;
      startHeightRef.current = height;
    } else {
      setDragging(false);
      startYRef.current = clientY;
    }
  };

  const onMouseMove = useCallback((e) => {
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);
    const delta = clientY - startYRef.current;

    if (dragging) {
      const slideDelta = startYRef.current - clientY;
      let newHeight = startHeightRef.current + slideDelta;
      newHeight = Math.max(collapsedHeight, Math.min(expandedHeight, newHeight));
      setHeight(newHeight);
    } else {
      const ul = listRef.current;
      if (!ul) return;

      const isAtTop = ul.scrollTop <= 0;
      const isAtBottom = ul.scrollTop + ul.clientHeight >= ul.scrollHeight;
      if (isAtTop && delta > 0) {
        const pad = Math.min(delta / 3, 40) + 10;
        ul.style.paddingTop = `${pad}px`;
      } else if (isAtBottom && delta < 0) {
        const pad = Math.min(Math.abs(delta) / 3, 40) + 10;
        ul.style.paddingBottom = `${pad}px`;
      }
    }
  }, [dragging, expandedHeight, collapsedHeight]);

  const onMouseUp = useCallback(() => {
    if (dragging) {
      setDragging(false);

      const delta = height - startHeightRef.current;

      if (startHeightRef.current === collapsedHeight && delta > threshold) {
        animateHeight(expandedHeight);
      } else if (startHeightRef.current === expandedHeight && delta < -threshold) {
        animateHeight(collapsedHeight);
      } else {
        animateHeight(startHeightRef.current);
      }
    }

    const ul = listRef.current;
    if (!ul) return;

    ul.style.paddingBottom = "10px";
    ul.style.paddingTop = "10px";
  }, [dragging, collapsedHeight, expandedHeight, height]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTime = () => setCurrentTimeSec(audio.currentTime || 0);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("touchmove", onMouseMove);
    window.addEventListener("touchend", onMouseUp);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("seeked", onTime);
    audio.addEventListener("loadedmetadata", onTime);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("seeked", onTime);
      audio.removeEventListener("loadedmetadata", onTime);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("touchmove", onMouseMove);
      window.removeEventListener("touchend", onMouseUp);
    };
  }, [onMouseMove, onMouseUp, setCurrentTimeSec, audioRef]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (playbackState === "playing") {
      audio.pause();
    } else {
      audio.play();
    }
  };

  const { handleParentTap, wrapChildClick } = useDoubleTap({
    onDoubleTap: () =>
      animateHeight(height === collapsedHeight ? expandedHeight : collapsedHeight)
  });

  const onTabChevron = () => {
    animateHeight(height === collapsedHeight ? expandedHeight : collapsedHeight);
  }

  return {
    playerRef,
    listRef,
    height,
    progress,
    collapsedOpacity,
    expandedOpacity,
    transitioning,
    bottomOffset: collapsedHeight * 0.15 * (1 - progress),
    onMouseDown,
    handleParentTap,
    wrapChildClick,
    onTabChevron,
    togglePlay,
  };
}
